import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageCircle, UserPlus, CheckCircle2, X, Loader, XCircle } from 'lucide-react';
import { Notification } from '../types';
import {
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  deleteAllReadNotifications,
  subscribeToUserNotifications,
} from '../api/notifications';

const Notifications = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToUserNotifications(currentUser.uid, (notifs) => {
      setNotifications(notifs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await markNotificationAsRead(notification.id);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate if action URL exists
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead(currentUser!.uid);
    } catch (error) {
      console.error('Error marking all as read:', error);
      alert('Failed to mark all notifications as read');
    }
  };

  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Failed to delete notification');
    }
  };

  const handleDeleteAllRead = async () => {
    if (!window.confirm('Delete all read notifications?')) return;
    try {
      await deleteAllReadNotifications(currentUser!.uid);
    } catch (error) {
      console.error('Error deleting all read notifications:', error);
      alert('Failed to delete read notifications');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'connection_request':
        return <UserPlus className="w-5 h-5 text-blue-400" />;
      case 'connection_accepted':
        return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case 'connection_rejected':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'new_message':
        return <MessageCircle className="w-5 h-5 text-purple-400" />;
      default:
        return <Bell className="w-5 h-5 text-purple-300" />;
    }
  };

  const formatTimeAgo = (timestamp: any) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-purple-200 text-lg">Please log in to view notifications</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 text-white px-4 py-6 md:py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Bell className="w-8 h-8 md:w-10 md:h-10 text-purple-300" />
              <h1 className="text-2xl md:text-3xl font-bold">Notifications</h1>
              {unreadCount > 0 && (
                <span className="px-3 py-1 bg-purple-500 text-white text-sm rounded-full">
                  {unreadCount} unread
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="px-4 py-2 bg-green-500/20 border border-green-400/50 rounded-lg text-green-200 hover:bg-green-500/30 transition text-sm"
                >
                  Mark all read
                </button>
              )}
              {notifications.filter(n => n.read).length > 0 && (
                <button
                  onClick={handleDeleteAllRead}
                  className="px-4 py-2 bg-red-500/20 border border-red-400/50 rounded-lg text-red-200 hover:bg-red-500/30 transition text-sm"
                >
                  Clear read
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg transition ${
                filter === 'all'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-purple-200 hover:bg-white/10'
              }`}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg transition ${
                filter === 'unread'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-purple-200 hover:bg-white/10'
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader className="w-12 h-12 text-purple-300 mx-auto animate-spin" />
            <p className="text-purple-200 mt-4">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl border border-purple-300/20">
            <Bell className="w-16 h-16 text-purple-300/50 mx-auto mb-4" />
            <p className="text-purple-200 text-lg">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </p>
            <p className="text-purple-300/70 text-sm mt-2">
              You're all caught up!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-white/5 backdrop-blur-lg border rounded-xl p-4 cursor-pointer transition hover:border-purple-400/50 ${
                  notification.read
                    ? 'border-purple-300/20 opacity-70'
                    : 'border-purple-300/30 bg-purple-500/5'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-semibold mb-1 ${notification.read ? 'text-purple-200' : 'text-purple-100'}`}>
                          {notification.title}
                        </h3>
                        <p className={`text-sm mb-2 ${notification.read ? 'text-purple-300/70' : 'text-purple-200/80'}`}>
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-purple-300/70">
                          <span>{formatTimeAgo(notification.createdAt)}</span>
                          {notification.actionLabel && (
                            <span className="text-purple-400 hover:text-purple-300">
                              {notification.actionLabel} →
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {!notification.read && (
                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                        )}
                        <button
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                          className="p-1 hover:bg-white/10 rounded transition text-purple-300/70 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
